import React, { useState, useEffect } from 'react';
import { X, Loader2, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_TYPES = [
  'Windshield Replacement',
  'Rock Chip Repair',
  'Door Glass',
  'Back Glass',
];

const INITIAL = {
  full_name: '',
  phone: '',
  email: '',
  service_type: '',
  vehicle: '',
  vin: '',
  service_location: '',
  description: '',
};

export default function QuoteRequestModal({ open, onClose }) {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!open) return;
    // Reset form when opening
    setForm(INITIAL);
    setErrors({});
    setSuccess(false);
    setSubmitError('');
    // Fetch locations
    axios.get(`${API}/public/locations`)
      .then((r) => setLocations(r.data || []))
      .catch(() => setLocations([]));
  }, [open]);

  // Lock body scroll
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  if (!open) return null;

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = 'Full name is required';
    if (!form.phone.trim()) e.phone = 'Phone number is required';
    else if (!/^[\d\s()+\-.]{7,}$/.test(form.phone.trim())) e.phone = 'Enter a valid phone number';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter a valid email';
    if (!form.service_type) e.service_type = 'Please choose a service';
    if (!form.vehicle.trim()) e.vehicle = 'Year, Make and Model are required';
    if (!form.service_location) e.service_location = 'Choose a service location';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setSubmitError('');
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/public/quote-requests`, {
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        service_type: form.service_type,
        vehicle: form.vehicle.trim(),
        vin: form.vin.trim() || null,
        service_location: form.service_location,
        description: form.description.trim() || null,
      });
      setSuccess(true);
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-[fadeIn_0.2s_ease-out]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="quote-modal-title"
    >
      <div
        className="relative w-full max-w-[560px] max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white px-7 pt-6 pb-4 border-b border-slate-100 rounded-t-2xl z-10">
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 w-9 h-9 rounded-full text-slate-500 hover:text-slate-800 hover:bg-slate-100 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="text-[11px] font-bold tracking-[0.24em] text-[#0D5BD7]">A+ AUTO GLASS</div>
          <h2 id="quote-modal-title" className="mt-1 text-[26px] font-black text-[#111827] leading-tight">
            Request a Quote
          </h2>
          <p className="mt-1 text-sm text-[#5F6773]">
            Tell us a little about your vehicle and we&apos;ll get right back to you.
          </p>
        </div>

        {/* Body */}
        {success ? (
          <div className="px-7 py-10 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h3 className="mt-4 text-xl font-bold text-[#111827]">Thanks &mdash; we got it!</h3>
            <p className="mt-2 text-sm text-[#5F6773] max-w-sm mx-auto">
              A member of our team will reach out shortly to confirm your quote details and schedule service.
            </p>
            <button
              onClick={onClose}
              className="mt-6 inline-flex items-center h-11 px-7 rounded-full bg-[#0D5BD7] hover:bg-[#0a4bb5] text-white text-[12px] font-bold tracking-[0.2em] transition-colors"
            >
              CLOSE
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-7 py-6 space-y-4" noValidate>
            <Field label="Full Name" required error={errors.full_name}>
              <input
                type="text"
                value={form.full_name}
                onChange={(e) => setField('full_name', e.target.value)}
                placeholder="John Smith"
                className={inputCls(!!errors.full_name)}
                autoComplete="name"
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Phone Number" required error={errors.phone}>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                  placeholder="613-555-0000"
                  className={inputCls(!!errors.phone)}
                  autoComplete="tel"
                />
              </Field>
              <Field label="Email Address" required error={errors.email}>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  placeholder="you@email.com"
                  className={inputCls(!!errors.email)}
                  autoComplete="email"
                />
              </Field>
            </div>

            <Field label="Service Type" required error={errors.service_type}>
              <select
                value={form.service_type}
                onChange={(e) => setField('service_type', e.target.value)}
                className={inputCls(!!errors.service_type)}
              >
                <option value="">Choose a service…</option>
                {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>

            <Field label="Year / Make / Model" required error={errors.vehicle}>
              <input
                type="text"
                value={form.vehicle}
                onChange={(e) => setField('vehicle', e.target.value)}
                placeholder="e.g. 2022 Honda CR-V"
                className={inputCls(!!errors.vehicle)}
              />
            </Field>

            <Field label="VIN Number" hint="(optional)">
              <input
                type="text"
                value={form.vin}
                onChange={(e) => setField('vin', e.target.value.toUpperCase())}
                placeholder="Enter VIN if known"
                className={`${inputCls(false)} font-mono tracking-wide`}
                maxLength={17}
              />
            </Field>

            <Field label="Service Location" required error={errors.service_location}>
              <select
                value={form.service_location}
                onChange={(e) => setField('service_location', e.target.value)}
                className={inputCls(!!errors.service_location)}
                data-testid="quote-service-location-select"
              >
                <option value="">Choose a location…</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
                <option value="mobile">Mobile Service (we come to you)</option>
              </select>
            </Field>

            <Field label="Description" hint="(optional)">
              <textarea
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                placeholder="Tell us anything else about your vehicle or the damage"
                rows={3}
                className={`${inputCls(false)} h-auto py-2.5 resize-none`}
              />
            </Field>

            {submitError && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            <div className="pt-2 flex items-center gap-3">
              <button
                type="submit"
                disabled={loading}
                className="group inline-flex items-center h-12 px-7 rounded-full bg-[#0D5BD7] hover:bg-[#0a4bb5] disabled:opacity-70 disabled:cursor-not-allowed text-white text-[12px] font-bold tracking-[0.2em] transition-all shadow-sm hover:shadow-md"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> SUBMITTING…</>
                ) : (
                  <>SUBMIT REQUEST <ArrowRight className="w-4 h-4 ml-3 transition-transform group-hover:translate-x-1" /></>
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-[#5F6773] hover:text-[#111827]"
              >
                Cancel
              </button>
            </div>
            <p className="text-[11px] text-slate-400 pt-1">
              By submitting, you agree to be contacted by A+ Auto Glass regarding your request.
            </p>
          </form>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
      `}</style>
    </div>
  );
}

function Label({ children, required }) {
  return (
    <label className="block text-[13px] font-semibold text-[#111827]">
      {children} {required && <span className="text-[#0D5BD7]">*</span>}
    </label>
  );
}

function Field({ label, required, hint, error, children }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label required={required}>{label}</Label>
        {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
      </div>
      <div className="mt-2">{children}</div>
      {error && (
        <div className="mt-1.5 flex items-center gap-1 text-xs text-rose-600">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

function inputCls(hasError) {
  return `w-full h-11 px-3.5 rounded-lg border ${hasError ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-200' : 'border-slate-200 focus:border-[#0D5BD7] focus:ring-blue-100'} bg-white text-[14px] text-[#111827] placeholder:text-slate-400 focus:outline-none focus:ring-4 transition-colors`;
}
